import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// ============ Types ============

export interface ConsentDocument {
  id: string;
  consent_type: string;
  version: string;
  title: string;
  content: string;
  summary: string | null;
  is_active: boolean;
  requires_explicit_consent: boolean;
  effective_date: string;
  created_at: string;
  updated_at: string;
}

export interface ConsentRecord {
  id: string;
  user_id: string;
  consent_document_id: string;
  consent_type: string;
  consent_version: string;
  consented: boolean;
  consent_text_snapshot: string;
  ip_address: string | null;
  user_agent: string | null;
  consented_at: string;
  revoked_at: string | null;
  revocation_reason: string | null;
}

export type ConsentType = 'hipaa_authorization' | 'privacy_policy' | 'terms_of_service' | 'data_processing';

// ============ Query Keys ============

export const consentKeys = {
  all: ['consent'] as const,
  documents: () => [...consentKeys.all, 'documents'] as const,
  document: (type: string) => [...consentKeys.all, 'document', type] as const,
  records: (userId: string) => [...consentKeys.all, 'records', userId] as const,
  userConsent: (userId: string, type: string) => [...consentKeys.all, 'user', userId, type] as const,
  pendingConsents: (userId: string) => [...consentKeys.all, 'pending', userId] as const,
};

// ============ Hooks ============

/**
 * Get all active consent documents
 */
export function useConsentDocuments() {
  return useQuery({
    queryKey: consentKeys.documents(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consent_documents')
        .select('*')
        .eq('is_active', true)
        .order('consent_type');
      
      if (error) throw error;
      return data as ConsentDocument[];
    },
  });
}

/**
 * Get a specific consent document by type
 */
export function useConsentDocument(type: ConsentType) {
  return useQuery({
    queryKey: consentKeys.document(type),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('consent_documents')
        .select('*')
        .eq('consent_type', type)
        .eq('is_active', true)
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      return data as ConsentDocument | null;
    },
  });
}

/**
 * Get all consent records for a user
 */
export function useUserConsentRecords(userId: string | undefined) {
  return useQuery({
    queryKey: consentKeys.records(userId || ''),
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('consent_records')
        .select('*')
        .eq('user_id', userId)
        .order('consented_at', { ascending: false });
      
      if (error) throw error;
      return data as ConsentRecord[];
    },
    enabled: !!userId,
  });
}

/**
 * Check if user has given consent for a specific type
 */
export function useHasConsent(userId: string | undefined, type: ConsentType) {
  return useQuery({
    queryKey: consentKeys.userConsent(userId || '', type),
    queryFn: async () => {
      if (!userId) return false;
      
      // Get the latest active consent document for this type
      const { data: doc, error: docError } = await supabase
        .from('consent_documents')
        .select('version')
        .eq('consent_type', type)
        .eq('is_active', true)
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (docError || !doc) return false;
      
      // Check if user has consented to this version
      const { data: record, error: recordError } = await supabase
        .from('consent_records')
        .select('id, consented, revoked_at')
        .eq('user_id', userId)
        .eq('consent_type', type)
        .eq('consent_version', doc.version)
        .maybeSingle();
      
      if (recordError) return false;
      
      return record?.consented && !record?.revoked_at;
    },
    enabled: !!userId,
  });
}

/**
 * Get list of consents the user still needs to give
 */
export function usePendingConsents(userId: string | undefined) {
  return useQuery({
    queryKey: consentKeys.pendingConsents(userId || ''),
    queryFn: async () => {
      if (!userId) return [];
      
      // Get all active consent documents that require explicit consent
      const { data: docs, error: docsError } = await supabase
        .from('consent_documents')
        .select('*')
        .eq('is_active', true)
        .eq('requires_explicit_consent', true);
      
      if (docsError || !docs) return [];
      
      // Get user's consent records
      const { data: records, error: recordsError } = await supabase
        .from('consent_records')
        .select('consent_type, consent_version, consented, revoked_at')
        .eq('user_id', userId);
      
      if (recordsError) return docs;
      
      // Find documents without valid consent
      const pending = docs.filter(doc => {
        const record = records?.find(
          r => r.consent_type === doc.consent_type && 
               r.consent_version === doc.version
        );
        return !record || !record.consented || record.revoked_at;
      });
      
      return pending as ConsentDocument[];
    },
    enabled: !!userId,
  });
}

/**
 * Record user consent
 */
export function useRecordConsent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({
      userId,
      document,
      consented,
    }: {
      userId: string;
      document: ConsentDocument;
      consented: boolean;
    }) => {
      const { data, error } = await supabase
        .from('consent_records')
        .upsert({
          user_id: userId,
          consent_document_id: document.id,
          consent_type: document.consent_type,
          consent_version: document.version,
          consented,
          consent_text_snapshot: document.content,
          user_agent: navigator.userAgent,
          consented_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,consent_type,consent_version',
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as ConsentRecord;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: consentKeys.records(variables.userId) });
      queryClient.invalidateQueries({ queryKey: consentKeys.userConsent(variables.userId, variables.document.consent_type as ConsentType) });
      queryClient.invalidateQueries({ queryKey: consentKeys.pendingConsents(variables.userId) });
    },
    onError: (error) => {
      console.error('Failed to record consent:', error);
      toast.error(t('consent.recordError', 'Failed to save consent'));
    },
  });
}

/**
 * Revoke user consent
 */
export function useRevokeConsent() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({
      recordId,
      userId,
      reason,
    }: {
      recordId: string;
      userId: string;
      reason?: string;
    }) => {
      const { data, error } = await supabase
        .from('consent_records')
        .update({
          revoked_at: new Date().toISOString(),
          revocation_reason: reason || null,
        })
        .eq('id', recordId)
        .eq('user_id', userId)
        .select()
        .single();
      
      if (error) throw error;
      return data as ConsentRecord;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: consentKeys.records(variables.userId) });
      queryClient.invalidateQueries({ queryKey: consentKeys.all });
      toast.success(t('consent.revokeSuccess', 'Consent withdrawn successfully'));
    },
    onError: (error) => {
      console.error('Failed to revoke consent:', error);
      toast.error(t('consent.revokeError', 'Failed to withdraw consent'));
    },
  });
}

/**
 * Record multiple consents at once (for signup flow)
 */
export function useRecordMultipleConsents() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({
      userId,
      consents,
    }: {
      userId: string;
      consents: Array<{ document: ConsentDocument; consented: boolean }>;
    }) => {
      const records = consents.map(c => ({
        user_id: userId,
        consent_document_id: c.document.id,
        consent_type: c.document.consent_type,
        consent_version: c.document.version,
        consented: c.consented,
        consent_text_snapshot: c.document.content,
        user_agent: navigator.userAgent,
        consented_at: new Date().toISOString(),
      }));

      const { data, error } = await supabase
        .from('consent_records')
        .upsert(records, {
          onConflict: 'user_id,consent_type,consent_version',
        })
        .select();
      
      if (error) throw error;
      return data as ConsentRecord[];
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: consentKeys.records(variables.userId) });
      queryClient.invalidateQueries({ queryKey: consentKeys.pendingConsents(variables.userId) });
    },
    onError: (error) => {
      console.error('Failed to record consents:', error);
      toast.error(t('consent.recordError', 'Failed to save consents'));
    },
  });
}
